import * as React from "react";
import {
  StatusChip,
  StatusProgress,
  statusRingClass,
  cn,
  type StatusExpressionState,
} from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/**
 * Verification harness (added by the screenshot-verify pass for #125 / PR #139).
 * Exercises every lifecycle state across the three coordinated affordances plus
 * the flip-in-place publish moment on a single stable chip node.
 */
const CANONICAL: StatusExpressionState[] = ["generating", "ready", "draft", "published", "failed"];
const ALIASES = ["Indexing", "Synced", "Live", "Error", "Connecting"];

function FlipInPlace() {
  // One stable chip; cycling the status must re-theme it in place, never remount.
  const cycle: StatusExpressionState[] = ["draft", "generating", "ready", "published"];
  const [i, setI] = React.useState(0);
  return (
    <div className="flex items-center gap-4">
      <StatusChip status={cycle[i]} />
      <button
        type="button"
        onClick={() => setI((n) => (n + 1) % cycle.length)}
        className="rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
      >
        Advance lifecycle → next: {cycle[(i + 1) % cycle.length]}
      </button>
    </div>
  );
}

function Card({ status, title }: { status: string; title: string }) {
  return (
    <div
      className={cn(
        "relative flex w-64 items-start gap-3 overflow-hidden rounded-xl border bg-card p-4 transition-colors duration-200 ease-smooth-out",
        // Full-perimeter status keyline + faint tint (no single-side accent).
        statusRingClass(status),
      )}
    >
      <StatusProgress status={status} className="absolute inset-x-0 top-0 rounded-none" />
      <div>
        <div className="text-sm font-medium text-foreground">{title}</div>
        <div className="mt-2">
          <StatusChip status={status} size="sm" />
        </div>
      </div>
    </div>
  );
}

export function StatusExpressionDemo() {
  return (
    <Section
      id="status-expression"
      title="StatusExpression"
      description="The Brain lifecycle vocabulary: StatusChip + statusRingClass + StatusProgress, all routed through one status→presentation map. Status is encoded in form (dot, color, full-perimeter keyline, progress), not just text. The publish moment flips the same chip in place and re-tones the whole card border."
    >
      <VariantBlock label="StatusChip — five canonical states">
        {CANONICAL.map((s) => (
          <StatusChip key={s} status={s} />
        ))}
      </VariantBlock>

      <VariantBlock label="StatusChip — domain-word aliases" caption="collapse onto the five canonical states">
        {ALIASES.map((s) => (
          <StatusChip key={s} status={s} />
        ))}
      </VariantBlock>

      <VariantBlock label="StatusProgress — indeterminate + determinate">
        <div className="w-64 space-y-3">
          <StatusProgress status="generating" />
          <StatusProgress status="generating" value={42} valueText="Indexing 3 of 7 pages" />
          <StatusProgress status="generating" value={88} valueText="Indexing 6 of 7 pages" />
        </div>
      </VariantBlock>

      <VariantBlock label="On a card — full-perimeter status keyline + progress + chip together">
        <div className="flex flex-wrap gap-4">
          <Card status="generating" title="Founder Podcast Ep. 4" />
          <Card status="ready" title="Q3 Strategy Note" />
          <Card status="published" title="Welcome Quest" />
          <Card status="failed" title="Broken Import" />
        </div>
      </VariantBlock>

      <VariantBlock label="Flip-in-place" caption="same chip node re-themes; never remounts">
        <FlipInPlace />
      </VariantBlock>
    </Section>
  );
}
